"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  type TooltipContentProps,
  type PieLabelRenderProps,
} from "recharts";
import { BillingService } from "@/services/BillingService";
import type { BillingExpensesCategoriesOutput } from "@/interfaces/billing";
import { ChartColors } from "@/util/chartColors";
import { Tags, Search } from "lucide-react";
import { numberToMoney } from "@/util/moneyConversion";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { BaseComponentProps } from "@/interfaces/dashboard";

export const ExpensesByCategoryChart: React.FC<BaseComponentProps> = ({
  monthYear,
  isLoading = false,
}) => {
  const [expenses, setExpenses] = useState<BillingExpensesCategoriesOutput[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"chart" | "list">("chart");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<BillingExpensesCategoriesOutput | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new BillingService();
        const response = await service.getExpensesCategories({ monthYear });
        setExpenses(response ?? []);
      } catch {
        setError("Falha ao carregar dados de despesas");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [monthYear]);

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: PieLabelRenderProps) => {
    const inner = Number(innerRadius ?? 0);
    const outer = Number(outerRadius ?? inner);
    const radius = inner + (outer - inner) * 0.5;
    const angle = -Number(midAngle ?? 0) * (Math.PI / 180);
    const labelCx = Number(cx ?? 0) + radius * Math.cos(angle);
    const labelCy = Number(cy ?? 0) + radius * Math.sin(angle);
    const percentage = Math.round(Number(percent ?? 0) * 100);

    return (
      <text
        x={labelCx}
        y={labelCy}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${percentage}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const data = payload[0].payload as BillingExpensesCategoriesOutput;
    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-bold">{data.tagName}</p>
        <p>Total gasto: {numberToMoney(data.totalPaid)}</p>
      </div>
    );
  };

  const filteredExpenses = expenses.filter((expense) =>
    expense.tagName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pieChartData =
    filteredExpenses as unknown as Record<string, unknown>[];

  const handleCategoryClick = (category: BillingExpensesCategoriesOutput) => {
    setSelectedCategory(category === selectedCategory ? null : category);
  };

  if (isLoading || loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Maiores Despesas por Categoria</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <Skeleton className="h-[300px] w-full rounded-md" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Maiores Despesas por Categoria</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px] text-red-500">
          {error}
        </CardContent>
      </Card>
    );
  }

  if (expenses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Maiores Despesas por Categoria</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px] text-muted-foreground">
          Nenhuma despesa registrada neste período
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center">
          <Tags className="h-5 w-5 mr-2 text-orange-500" />
          Despesas por Categoria
        </CardTitle>
        <div className="flex space-x-1">
          <Button
            variant={viewMode === "chart" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("chart")}
            className={
              viewMode === "chart" ? "bg-orange-500 hover:bg-orange-600" : ""
            }
          >
            Gráfico
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className={
              viewMode === "list" ? "bg-orange-500 hover:bg-orange-600" : ""
            }
          >
            Lista
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {viewMode === "list" && (
          <div className="mb-4 relative">
            <Input
              placeholder="Buscar categoria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          </div>
        )}

        <AnimatePresence mode="wait">
          {viewMode === "chart" ? (
            <motion.div
              key="chart"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="totalPaid"
                    nameKey="tagName"
                    animationDuration={800}
                    animationBegin={0}
                  >
                    {filteredExpenses.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={ChartColors[index % ChartColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={CustomTooltip} />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: "20px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-[300px] overflow-auto pr-2"
            >
              <div className="space-y-2">
                {filteredExpenses.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[250px] text-muted-foreground">
                    <Tags className="h-12 w-12 mb-2 text-muted-foreground/50" />
                    <p>Nenhuma categoria encontrada</p>
                  </div>
                ) : (
                  filteredExpenses.map((category, index) => (
                    <motion.div
                      key={category.tagId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      onClick={() => handleCategoryClick(category)}
                      className={`p-3 border rounded-md cursor-pointer transition-all ${
                        selectedCategory?.tagId === category.tagId
                          ? "border-orange-500 bg-orange-50"
                          : "hover:border-gray-300"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{
                              backgroundColor:
                                ChartColors[index % ChartColors.length],
                            }}
                          />
                          <span className="font-medium">
                            {category.tagName}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-red-500">
                          {numberToMoney(category.totalPaid)}
                        </Badge>
                      </div>

                      {selectedCategory?.tagId === category.tagId && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-2 pt-2 border-t text-sm"
                        >
                          <div className="grid grid-cols-1 gap-2">
                            <div>
                              <p className="text-muted-foreground">
                                ID da Categoria:
                              </p>
                              <p className="font-semibold">{category.tagId}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};
